(function () {
  'use strict';

  // Remove no-js class immediately
  document.documentElement.classList.remove('no-js');

  // ──────────────────────────────────────────────
  // Utilities
  // ──────────────────────────────────────────────

  /**
   * Escape HTML special characters to prevent XSS when
   * inserting dynamic data via innerHTML.
   */
  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Validate that a URL is a safe image source (https from Shopify CDN).
   */
  function safeImageUrl(url) {
    if (typeof url !== 'string') return '';
    if (url.indexOf('https://') === 0) return url;
    if (url.indexOf('//') === 0) return 'https:' + url;
    return '';
  }

  /**
   * Format a price in cents using the store's money format.
   * Supports {{amount}}, {{amount_no_decimals}},
   * {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}}
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    var fmt = format || '${{amount}}';
    var value = (cents / 100).toFixed(2);
    var valueParts = value.split('.');

    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, valueParts[0])
      .replace(
        /\{\{\s*amount_with_comma_separator\s*\}\}/,
        valueParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + valueParts[1]
      )
      .replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
        valueParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      );
  }

  // ──────────────────────────────────────────────
  // Cart API
  // ──────────────────────────────────────────────

  var CartAPI = {
    get: function () {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    },

    add: function (id, quantity, sellingPlan) {
      var item = { id: parseInt(id, 10), quantity: quantity || 1 };
      if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
      return fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Error'); });
          return r.json();
        })
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('cart:updated'));
          return data;
        });
    },

    change: function (key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: String(key), quantity: quantity })
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not update cart'); });
          return r.json();
        })
        .then(function (data) {
          document.dispatchEvent(new CustomEvent('cart:updated'));
          return data;
        });
    }
  };

  // Expose for inline scripts
  window.CartAPI = CartAPI;
  window.formatMoney = formatMoney;

  // ──────────────────────────────────────────────
  // Cart Drawer
  // ──────────────────────────────────────────────

  function initCartDrawer() {
    var drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    var overlay = drawer.querySelector('.cart-drawer__overlay');
    var closeBtn = drawer.querySelector('.cart-drawer__close');
    var continueBtns = drawer.querySelectorAll('.cart-drawer__continue');

    function openDrawer() {
      drawer.classList.add('cart-drawer--open');
      document.body.classList.add('cart-drawer-open');
      document.documentElement.classList.add('scroll-locked');
      drawer.setAttribute('aria-hidden', 'false');

      // Focus trap: focus first focusable element
      var focusable = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusable.length) focusable[0].focus();
    }

    function closeDrawer() {
      drawer.classList.remove('cart-drawer--open');
      document.body.classList.remove('cart-drawer-open');
      document.documentElement.classList.remove('scroll-locked');
      drawer.setAttribute('aria-hidden', 'true');
      var cartLink = document.querySelector('.header__cart-link');
      if (cartLink) cartLink.focus();
    }

    // Focus trap: keep Tab cycling within drawer
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      var focusable = drawer.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    if (overlay) overlay.addEventListener('click', closeDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    continueBtns.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('cart-drawer--open')) {
        closeDrawer();
      }
    });

    // Quantity buttons inside drawer
    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-drawer-qty]');
      if (!btn) return;
      var key = btn.dataset.lineKey;
      var action = btn.dataset.drawerQty;
      var input = drawer.querySelector('input[data-key="' + key + '"]');
      if (!input) return;
      var val = parseInt(input.value, 10) || 1;
      var newQty = action === 'increase' ? val + 1 : Math.max(val - 1, 0);
      input.value = newQty;
      CartAPI.change(key, newQty).catch(function (err) {
        console.error('Cart update error:', err);
      });
    });

    // Remove buttons inside drawer
    drawer.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('[data-drawer-remove]');
      if (!removeBtn) return;
      CartAPI.change(removeBtn.dataset.lineKey, 0).catch(function (err) {
        console.error('Cart remove error:', err);
      });
    });

    // Discount code
    var discountInput = drawer.querySelector('#cart-discount-input');
    var discountApplyBtn = drawer.querySelector('#cart-discount-apply');
    var discountApplied = drawer.querySelector('#cart-discount-applied');
    var checkoutBtn = drawer.querySelector('#cart-checkout-btn');
    var appliedCode = '';

    function applyDiscountCode() {
      var code = discountInput ? discountInput.value.trim() : '';
      if (!code) return;
      appliedCode = code;
      if (checkoutBtn) {
        checkoutBtn.href = '/checkout?discount=' + encodeURIComponent(code);
      }
      if (discountApplied) {
        var s2 = (window.theme && window.theme.strings) || {};
        discountApplied.innerHTML =
          '<span>' + escapeHtml((s2.discountApplied || 'Code applied:') + ' ' + code) + '</span>'
          + '<button type="button" class="cart-drawer__discount-remove" id="cart-discount-remove">'
          + escapeHtml(s2.discountRemove || 'Remove')
          + '</button>';
        discountApplied.style.display = 'flex';
      }
    }

    function removeDiscountCode() {
      appliedCode = '';
      if (discountInput) discountInput.value = '';
      if (checkoutBtn) checkoutBtn.href = '/checkout';
      if (discountApplied) {
        discountApplied.innerHTML = '';
        discountApplied.style.display = 'none';
      }
    }

    if (discountApplyBtn) {
      discountApplyBtn.addEventListener('click', applyDiscountCode);
    }
    if (discountInput) {
      discountInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyDiscountCode(); }
      });

      // Optional: auto-uppercase as user types (most discount codes are upper-case)
      var autoUpper = drawer.dataset.discountAutoUppercase === '1';
      if (autoUpper) {
        discountInput.addEventListener('input', function () {
          var pos = discountInput.selectionStart;
          var upper = discountInput.value.toUpperCase();
          if (upper !== discountInput.value) {
            discountInput.value = upper;
            try { discountInput.setSelectionRange(pos, pos); } catch (err) {}
          }
        });
      }

      // Optional: auto-apply with debounce (user doesn't need to click Apply)
      var autoApply = drawer.dataset.discountAutoApply === '1';
      if (autoApply) {
        var delay = parseInt(drawer.dataset.discountAutoApplyDelay, 10) || 600;
        var debounceTimer = null;
        discountInput.addEventListener('input', function () {
          if (debounceTimer) window.clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(function () {
            if (discountInput.value.trim()) applyDiscountCode();
          }, delay);
        });
      }
    }
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('#cart-discount-remove')) removeDiscountCode();
    });

    // Listen for cart updates and re-render
    document.addEventListener('cart:updated', function () {
      CartAPI.get()
        .then(function (cart) {
          renderCartDrawer(cart);
          updateCartCount(cart.item_count);
        })
        .catch(function (err) {
          console.error('Cart refresh error:', err);
        });
    });

    // Open drawer on custom event
    document.addEventListener('cart:open', openDrawer);

    // Expose for programmatic use
    window.openCartDrawer = openDrawer;
    window.closeCartDrawer = closeDrawer;
  }

  function renderCartDrawer(cart) {
    var itemsContainer = document.querySelector('.cart-drawer__items');
    var subtotalEl = document.querySelector('.cart-drawer__subtotal-value');
    var footerEl = document.querySelector('.cart-drawer__footer');
    var emptyEl = document.querySelector('.cart-drawer__empty');
    if (!itemsContainer) return;

    var s = (window.theme && window.theme.strings) || {};

    if (!cart.items || cart.items.length === 0) {
      itemsContainer.innerHTML = '';
      if (footerEl) footerEl.style.display = 'none';
      if (emptyEl) emptyEl.style.display = 'block';
      return;
    }

    if (footerEl) footerEl.style.display = '';
    if (emptyEl) emptyEl.style.display = 'none';

    var html = cart.items.map(function (item) {
      var rawUrl = item.featured_image && item.featured_image.url
        ? item.featured_image.url.replace(/(\.\w+)(\?|$)/, '_120x120$1$2')
        : '';
      var imgSrc = safeImageUrl(rawUrl);
      var title = escapeHtml(item.product_title);
      var variantTitle = escapeHtml(item.variant_title || '');
      var lineKey = escapeHtml(String(item.key));
      var qty = parseInt(item.quantity, 10) || 0;
      var subLabel = item.selling_plan_allocation ? escapeHtml(item.selling_plan_allocation.selling_plan.name) : '';

      // Detect discount: compare original vs final price (covers both line & cart-level discounts)
      var lineDiscounts = item.line_level_discount_allocations || [];
      var hasPriceDiff = item.original_line_price && item.final_line_price && item.original_line_price > item.final_line_price;
      var hasDiscount = hasPriceDiff || lineDiscounts.length > 0;

      // Build price HTML
      var priceHtml;
      if (hasDiscount) {
        priceHtml = '<p class="cart-drawer__item-price">'
          + '<span class="cart-drawer__item-price--sale">' + formatMoney(item.final_line_price) + '</span>'
          + ' <s class="cart-drawer__item-price--compare">' + formatMoney(item.original_line_price) + '</s>'
          + '</p>';

        // Show named discount labels (line-level allocations)
        if (lineDiscounts.length > 0) {
          priceHtml += lineDiscounts.map(function (d) {
            return '<p class="cart-drawer__item-discount"><i class="fa-solid fa-tag" aria-hidden="true"></i> '
              + escapeHtml(d.discount_application.title) + ' (-' + formatMoney(d.amount) + ')</p>';
          }).join('');
        } else if (hasPriceDiff) {
          // Cart-level discount — no named allocation on item, show savings amount
          var savedAmt = item.original_line_price - item.final_line_price;
          priceHtml += '<p class="cart-drawer__item-discount"><i class="fa-solid fa-tag" aria-hidden="true"></i> '
            + escapeHtml(s.discountSaving || 'Discount') + ' (-' + formatMoney(savedAmt) + ')</p>';
        }
      } else {
        priceHtml = '<p class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</p>';
      }

      return '<div class="cart-drawer__item" data-key="' + lineKey + '">'
        + (imgSrc ? '<img class="cart-drawer__item-img" src="' + imgSrc + '" alt="' + title + '" width="60" height="60" loading="lazy">' : '<div class="cart-drawer__item-img cart-drawer__item-img--empty"></div>')
        + '<div class="cart-drawer__item-info">'
        + '<p class="cart-drawer__item-title">' + title + '</p>'
        + (variantTitle ? '<p class="cart-drawer__item-variant">' + variantTitle + '</p>' : '')
        + (subLabel ? '<p class="cart-drawer__item-sub"><i class="fa-solid fa-arrows-rotate" aria-hidden="true"></i> ' + subLabel + '</p>' : '')
        + '<div class="cart-drawer__item-qty">'
        + '<button type="button" class="cart-drawer__qty-btn" data-drawer-qty="decrease" data-line-key="' + lineKey + '" aria-label="-"><i class="fa-solid fa-minus" aria-hidden="true"></i></button>'
        + '<input type="number" class="cart-drawer__qty-input" value="' + qty + '" min="0" data-key="' + lineKey + '" aria-label="' + escapeHtml(s.quantity || 'Qty') + '" readonly>'
        + '<button type="button" class="cart-drawer__qty-btn" data-drawer-qty="increase" data-line-key="' + lineKey + '" aria-label="+"><i class="fa-solid fa-plus" aria-hidden="true"></i></button>'
        + '</div>'
        + '</div>'
        + '<div class="cart-drawer__item-right">'
        + priceHtml
        + '<button type="button" class="cart-drawer__remove" data-drawer-remove data-line-key="' + lineKey + '" aria-label="' + escapeHtml(s.remove || 'Remove') + '"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>'
        + '</div>'
        + '</div>';
    }).join('');

    itemsContainer.innerHTML = html;
    // Subtotal — show original price crossed out when any discount is active
    var hasAnyDiscount = cart.total_discount && cart.total_discount > 0;
    if (subtotalEl) {
      if (hasAnyDiscount) {
        var originalTotal = cart.original_total_price || (cart.total_price + cart.total_discount);
        subtotalEl.innerHTML = '<span class="cart-drawer__subtotal-sale">' + formatMoney(cart.total_price) + '</span>'
          + ' <s class="cart-drawer__subtotal-compare">' + formatMoney(originalTotal) + '</s>';
      } else {
        subtotalEl.textContent = formatMoney(cart.total_price);
      }
    }

    // Cart-level discounts (automatic, scripts, discount codes applied server-side)
    var discountsEl = document.getElementById('cart-discounts');
    if (discountsEl) {
      var discountLines = [];

      // 1. Named cart-level discount applications (automatic + script discounts)
      if (cart.cart_level_discount_applications && cart.cart_level_discount_applications.length > 0) {
        cart.cart_level_discount_applications.forEach(function (d) {
          discountLines.push(
            '<div class="cart-drawer__discount-line">'
            + '<span><i class="fa-solid fa-tag" aria-hidden="true"></i> ' + escapeHtml(d.title) + '</span>'
            + '<span class="cart-drawer__discount-amount">-' + formatMoney(d.total_allocated_amount) + '</span>'
            + '</div>'
          );
        });
      }

      // 2. If total_discount > 0 but no cart_level applications, show generic discount
      if (discountLines.length === 0 && hasAnyDiscount) {
        // Sum up all line-level allocations
        var lineLevelTotal = 0;
        cart.items.forEach(function (item) {
          if (item.line_level_discount_allocations) {
            item.line_level_discount_allocations.forEach(function (a) {
              lineLevelTotal += a.amount || 0;
            });
          }
        });
        // Remaining discount not explained by line-level = cart-level
        var unexplainedDiscount = cart.total_discount - lineLevelTotal;
        if (unexplainedDiscount > 0) {
          discountLines.push(
            '<div class="cart-drawer__discount-line">'
            + '<span><i class="fa-solid fa-tag" aria-hidden="true"></i> ' + escapeHtml(s.discountSaving || 'Discount') + '</span>'
            + '<span class="cart-drawer__discount-amount">-' + formatMoney(unexplainedDiscount) + '</span>'
            + '</div>'
          );
        }
      }

      if (discountLines.length > 0) {
        discountsEl.innerHTML = discountLines.join('');
        discountsEl.style.display = '';
      } else {
        discountsEl.innerHTML = '';
        discountsEl.style.display = 'none';
      }
    }

    // Total savings banner — shows for ANY discount type
    var savingsEl = document.getElementById('cart-savings');
    if (savingsEl) {
      if (hasAnyDiscount) {
        savingsEl.innerHTML = '<i class="fa-solid fa-piggy-bank" aria-hidden="true"></i> '
          + escapeHtml(s.totalSavings || 'You save') + ' '
          + '<strong>' + formatMoney(cart.total_discount) + '</strong>';
        savingsEl.style.display = '';
      } else {
        savingsEl.innerHTML = '';
        savingsEl.style.display = 'none';
      }
    }

    // Upsells: feed from first cart item's product
    if (cart.items && cart.items.length > 0) {
      fetchCartUpsells(cart.items[0].product_id);
    } else {
      var upsellEl = document.getElementById('cart-upsell');
      if (upsellEl) upsellEl.style.display = 'none';
    }

    // Shipping progress bar
    updateShippingBar(cart);

    // Hide express checkout block if no methods rendered (e.g. browser without Apple Pay)
    syncExpressCheckoutVisibility();
  }

  // ──────────────────────────────────────────────
  // Cart urgency countdown (e.g. "Cart reserved for 9:42")
  // Persists start time in localStorage so the user sees a consistent timer
  // across drawer opens/closes. Resets when cart goes empty.
  // ──────────────────────────────────────────────
  function initCartUrgencyCountdown() {
    var widgets = document.querySelectorAll('.cart-drawer__urgency[data-urgency-countdown]');
    if (!widgets.length) return;

    var STORAGE_KEY = 'bfx-cart-timer-start';

    function readStart() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        var n = parseInt(raw, 10);
        return isNaN(n) ? null : n;
      } catch (e) { return null; }
    }

    function writeStart(ts) {
      try { window.localStorage.setItem(STORAGE_KEY, String(ts)); } catch (e) {}
    }

    function clearStart() {
      try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }

    function format(ms) {
      if (ms < 0) ms = 0;
      var totalS = Math.floor(ms / 1000);
      var m = Math.floor(totalS / 60);
      var s = totalS % 60;
      return m + ':' + (s < 10 ? '0' : '') + s;
    }

    widgets.forEach(function (widget) {
      var minutes = parseInt(widget.dataset.urgencyCountdown, 10);
      if (!minutes || minutes <= 0) return;
      var template = widget.dataset.urgencyTextTemplate || '';
      var textEl = widget.querySelector('.cart-drawer__urgency-text');
      var totalMs = minutes * 60 * 1000;

      function tick() {
        var start = readStart();
        if (!start) {
          start = Date.now();
          writeStart(start);
        }
        var remaining = (start + totalMs) - Date.now();
        if (remaining <= 0) {
          widget.style.display = 'none';
          clearStart();
          return false;
        }
        if (textEl) {
          textEl.textContent = template.replace('{time}', format(remaining));
        }
        return true;
      }

      tick();
      var intervalId = window.setInterval(function () {
        if (!tick()) window.clearInterval(intervalId);
      }, 1000);
    });

    // Reset timer when cart goes empty (e.g. after checkout completes elsewhere)
    document.addEventListener('cart:updated', function () {
      if (!window.CartAPI) return;
      window.CartAPI.get().then(function (cart) {
        if (cart && cart.item_count === 0) clearStart();
      }).catch(function () {});
    });
  }

  // Hide the express-checkout container when its form has no rendered buttons.
  // Shopify's payment_button outputs nothing if the user's browser/region has no
  // express methods available, leaving an awkward divider with no buttons.
  function syncExpressCheckoutVisibility() {
    var blocks = document.querySelectorAll('.cart-drawer__express');
    blocks.forEach(function (block) {
      var form = block.querySelector('form');
      if (!form) { block.style.display = 'none'; return; }
      var hasButtons = form.querySelector('button, [role="button"]');
      block.style.display = hasButtons ? '' : 'none';
    });
  }

  function updateCartCount(count) {
    var badges = document.querySelectorAll('.cart-count');
    badges.forEach(function (b) {
      b.textContent = count;
      if (count > 0) {
        b.style.display = '';
      } else {
        b.style.display = 'none';
      }
    });
  }

  // ──────────────────────────────────────────────
  // Mobile menu toggle
  // ──────────────────────────────────────────────
  function initMobileMenu() {
    var menuBtn = document.getElementById('menu-btn');
    var navLinks = document.getElementById('nav-links');
    if (!menuBtn || !navLinks) return;

    menuBtn.addEventListener('click', function () {
      var expanded = menuBtn.getAttribute('aria-expanded') === 'true';
      menuBtn.setAttribute('aria-expanded', String(!expanded));
      navLinks.classList.toggle('nav-links--open');
      document.body.classList.toggle('menu-open');
    });

    // Close on outside click
    document.addEventListener('click', function (e) {
      if (
        navLinks.classList.contains('nav-links--open') &&
        !navLinks.contains(e.target) &&
        !menuBtn.contains(e.target)
      ) {
        menuBtn.setAttribute('aria-expanded', 'false');
        navLinks.classList.remove('nav-links--open');
        document.body.classList.remove('menu-open');
      }
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && navLinks.classList.contains('nav-links--open')) {
        menuBtn.setAttribute('aria-expanded', 'false');
        navLinks.classList.remove('nav-links--open');
        document.body.classList.remove('menu-open');
        menuBtn.focus();
      }
    });
  }

  // ──────────────────────────────────────────────
  // Sticky header scroll shadow
  // ──────────────────────────────────────────────
  function initStickyHeader() {
    var header = document.getElementById('site-header');
    if (!header || header.dataset.sticky !== 'true') return;

    window.addEventListener(
      'scroll',
      function () {
        header.classList.toggle('header--scrolled', window.scrollY > 20);
      },
      { passive: true }
    );
  }

  // ──────────────────────────────────────────────
  // Theme toggle (light / dark)
  // ──────────────────────────────────────────────
  function initThemeToggle() {
    var toggleBtn = document.getElementById('theme-toggle');
    if (!toggleBtn) return;

    toggleBtn.addEventListener('click', function () {
      var current = document.documentElement.getAttribute('data-theme');
      var next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      try {
        sessionStorage.setItem('theme', next);
      } catch (_) {}
    });
  }

  // ──────────────────────────────────────────────
  // Product image gallery
  // ──────────────────────────────────────────────
  function initProductGallery() {
    var thumbs = document.querySelectorAll('.product-thumb');
    if (!thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var mainImg = document.querySelector('.product-main-image img');
        if (mainImg && thumb.dataset.full) {
          mainImg.src = thumb.dataset.full;
          if (thumb.dataset.srcset) {
            mainImg.srcset = thumb.dataset.srcset;
          }
        }
        thumbs.forEach(function (t) { t.classList.remove('product-thumb--active'); });
        thumb.classList.add('product-thumb--active');
      });
    });
  }

  // ──────────────────────────────────────────────
  // Announcement bar close
  // ──────────────────────────────────────────────
  function initAnnouncementBar() {
    var closeBtn = document.querySelector('.announcement-bar__close');
    var bar = document.querySelector('.announcement-bar');
    if (!closeBtn || !bar) return;

    closeBtn.addEventListener('click', function () {
      bar.setAttribute('aria-hidden', 'true');
      bar.style.display = 'none';
      try {
        sessionStorage.setItem('announcement-dismissed', '1');
      } catch (_) {}
    });

    try {
      if (sessionStorage.getItem('announcement-dismissed') === '1') {
        bar.setAttribute('aria-hidden', 'true');
        bar.style.display = 'none';
      }
    } catch (_) {}
  }

  // ──────────────────────────────────────────────
  // AJAX Add-to-Cart interception on product forms
  // ──────────────────────────────────────────────
  function initAjaxAddToCart() {
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      e.preventDefault();
      var idInput = form.querySelector('input[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (!idInput) return;

      var id = idInput.value;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var submitBtn = form.querySelector('[type="submit"]');

      // Double-submit guard
      if (submitBtn && submitBtn.disabled) return;

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.itemAdded) || 'Added!';
      }

      CartAPI.add(id, qty)
        .then(function () {
          document.dispatchEvent(new CustomEvent('cart:open'));
          if (submitBtn) {
            setTimeout(function () {
              submitBtn.disabled = false;
              submitBtn.textContent = submitBtn.dataset.addText || ((window.theme && window.theme.strings && window.theme.strings.addToCart) || 'Add to cart');
            }, 1500);
          }
        })
        .catch(function (err) {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = (window.theme && window.theme.strings && window.theme.strings.addToCartError) || 'Try again';
            setTimeout(function () {
              submitBtn.textContent = submitBtn.dataset.addText || 'Add to cart';
            }, 2000);
          }
          console.error('Add to cart error:', err.message);
        });
    });
  }

  // ──────────────────────────────────────────────
  // Variant-link Add-to-Cart (plan cards, hero CTAs, etc.)
  // Intercepts clicks on any [data-variant-id] element and
  // uses AJAX cart instead of navigating to /cart/id:qty
  // ──────────────────────────────────────────────
  function initVariantLinkAddToCart() {
    document.addEventListener('click', function (e) {
      var el = e.target.closest('[data-variant-id]');
      if (!el) return;
      var variantId = el.dataset.variantId;
      if (!variantId) return;

      // Double-click guard: ignore while a request for this element is in flight
      if (el.getAttribute('aria-busy') === 'true') { e.preventDefault(); return; }

      var sellingPlanId = el.dataset.sellingPlanId || null;

      e.preventDefault();
      el.classList.add('is-loading');
      el.classList.remove('is-error');
      el.setAttribute('aria-busy', 'true');

      CartAPI.add(variantId, 1, sellingPlanId)
        .then(function () {
          el.classList.remove('is-loading');
          el.removeAttribute('aria-busy');
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function (err) {
          el.classList.remove('is-loading');
          el.removeAttribute('aria-busy');
          el.classList.add('is-error');
          console.error('Add to cart error:', err.message);

          // Don't leave the customer on a dead click. If this is a real link
          // (plan card / hero CTA), fall back to navigating to its href.
          var href = el.getAttribute('href');
          if (el.tagName === 'A' && href && href !== '#') {
            window.location.href = href;
            return;
          }
          // Otherwise surface a brief, visible message next to the control.
          showAddToCartError(el);
        });
    });
  }

  // Insert a transient error message after a control whose add-to-cart failed.
  function showAddToCartError(el) {
    var msg = (window.theme && window.theme.strings && window.theme.strings.addToCartError)
      || 'Could not add to cart. Please try again.';
    var existing = el.parentNode && el.parentNode.querySelector('[data-atc-error]');
    if (existing) existing.remove();
    var note = document.createElement('span');
    note.setAttribute('data-atc-error', '');
    note.setAttribute('role', 'alert');
    note.textContent = msg;
    note.style.cssText = 'display:block;margin-top:0.5rem;font-size:0.8125rem;color:#DC2626;';
    if (el.parentNode) el.parentNode.insertBefore(note, el.nextSibling);
    setTimeout(function () { note.remove(); el.classList.remove('is-error'); }, 4000);
  }

  // ──────────────────────────────────────────────
  // Cart icon → open drawer instead of navigate
  // ──────────────────────────────────────────────
  function initCartIconDrawer() {
    var cartLink = document.querySelector('.header__cart-link');
    if (!cartLink || !document.getElementById('cart-drawer')) return;

    cartLink.addEventListener('click', function (e) {
      e.preventDefault();
      CartAPI.get()
        .then(function (cart) {
          renderCartDrawer(cart);
          updateCartCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('cart:open'));
        })
        .catch(function (err) {
          console.error('Cart load error:', err);
          // Fallback: navigate to cart page
          window.location.href = cartLink.href;
        });
    });
  }

  // ──────────────────────────────────────────────
  // Cart upsell — Product Recommendations API
  // ──────────────────────────────────────────────
  function fetchCartUpsells(productId) {
    var container = document.getElementById('cart-upsell');
    var itemsEl = document.getElementById('cart-upsell-items');
    if (!container || !itemsEl || !productId) {
      if (container) container.style.display = 'none';
      return;
    }

    fetch('/recommendations/products.json?product_id=' + productId + '&limit=3&intent=cart')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!data.products || !data.products.length) {
          container.style.display = 'none';
          return;
        }
        var s = (window.theme && window.theme.strings) || {};
        var html = data.products.map(function (p) {
          var rawUrl = p.featured_image
            ? String(p.featured_image).replace(/(\.\w+)(\?|$)/, '_80x80$1$2')
            : '';
          var imgSrc = safeImageUrl(rawUrl);
          var title = escapeHtml(p.title);
          var variant = p.variants && p.variants[0];
          var variantId = variant ? variant.id : '';
          var available = variant ? variant.available : false;
          var price = formatMoney(variant ? variant.price : p.price);
          return '<div class="cart-upsell__item">'
            + (imgSrc
              ? '<img class="cart-upsell__img" src="' + imgSrc + '" alt="' + title + '" width="44" height="44" loading="lazy">'
              : '<div class="cart-upsell__img cart-upsell__img--empty"></div>')
            + '<div class="cart-upsell__info">'
            + '<span class="cart-upsell__title">' + title + '</span>'
            + '<span class="cart-upsell__price">' + price + '</span>'
            + '</div>'
            + (available && variantId
              ? '<button type="button" class="cart-upsell__add" data-variant-id="' + variantId + '" aria-label="' + escapeHtml(s.addToCart || 'Add') + '"><i class="fa-solid fa-plus" aria-hidden="true"></i></button>'
              : '<span class="cart-upsell__unavailable">—</span>')
            + '</div>';
        }).join('');
        itemsEl.innerHTML = html;
        container.style.display = '';
      })
      .catch(function () {
        if (container) container.style.display = 'none';
      });
  }

  // ──────────────────────────────────────────────
  // Free shipping progress bar
  // ──────────────────────────────────────────────
  function updateShippingBar(cart) {
    var bar = document.getElementById('cart-shipping-bar');
    var msg = document.getElementById('cart-shipping-msg');
    var fill = document.getElementById('cart-shipping-fill');
    var drawer = document.getElementById('cart-drawer');
    var dataThreshold = drawer ? parseInt(drawer.dataset.freeShippingThreshold, 10) : 0;
    var threshold = dataThreshold || (window.theme && window.theme.freeShippingThreshold) || 0;

    if (!bar || !threshold) {
      if (bar) bar.style.display = 'none';
      return;
    }

    var total = cart.total_price || 0;
    var remaining = Math.max(threshold - total, 0);
    var pct = Math.min(Math.round((total / threshold) * 100), 100);
    var s = (window.theme && window.theme.strings) || {};

    bar.style.display = '';
    if (fill) {
      fill.style.width = pct + '%';
      fill.classList.toggle('cart-drawer__shipping-fill--unlocked', remaining === 0);
    }
    if (msg) {
      if (remaining > 0) {
        msg.innerHTML = escapeHtml(s.shippingBarProgress || 'Add {amount} for FREE shipping')
          .replace('{amount}', '<strong>' + escapeHtml(formatMoney(remaining)) + '</strong>');
      } else {
        msg.textContent = s.shippingBarUnlocked || "You've unlocked FREE shipping!";
      }
    }
  }

  // ──────────────────────────────────────────────
  // Plan card subscribe/one-time toggle
  // Supports per-card buttons AND a global section-level toggle.
  // Global toggle propagates the chosen type to every card in the section.
  // ──────────────────────────────────────────────
  function applyPurchaseTypeToCard(card, type) {
    if (!card) return;
    var cta = card.querySelector('.plan-card__cta');
    var onetimePrice = card.querySelector('.plan-card__price-onetime');
    var subPrice = card.querySelector('.plan-card__price-subscribe');
    var subBadge = card.querySelector('.plan-card__sub-badge');
    var sellingPlanId = card.dataset.sellingPlanId;

    // If this card has no selling plan configured, force onetime regardless.
    if (!sellingPlanId) type = 'onetime';

    if (type === 'subscribe') {
      if (cta && sellingPlanId) cta.dataset.sellingPlanId = sellingPlanId;
      if (cta && cta.dataset.ctaTextSubscribe) {
        cta.textContent = cta.dataset.ctaTextSubscribe;
      }
      if (onetimePrice) onetimePrice.style.display = 'none';
      if (subPrice) subPrice.style.display = '';
      if (subBadge) subBadge.style.display = '';
      card.classList.add('plan-card--subscribing');
    } else {
      if (cta) delete cta.dataset.sellingPlanId;
      if (cta && cta.dataset.ctaTextOnetime) {
        cta.textContent = cta.dataset.ctaTextOnetime;
      }
      if (onetimePrice) onetimePrice.style.display = '';
      if (subPrice) subPrice.style.display = 'none';
      if (subBadge) subBadge.style.display = 'none';
      card.classList.remove('plan-card--subscribing');
    }

    // Sync the per-card toggle buttons (so global → per-card visuals stay in sync).
    card.querySelectorAll('.plan-card__type-btn').forEach(function (b) {
      b.classList.toggle('plan-card__type-btn--active', b.dataset.type === type);
    });
  }

  function initPlanCardSubscribeToggle() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.plan-card__type-btn, .product-plans__toggle-btn');
      if (!btn) return;

      var type = btn.dataset.type;
      if (!type) return;

      var section = btn.closest('.product-plans');
      if (!section) return;

      var isGlobal = btn.classList.contains('product-plans__toggle-btn');

      if (isGlobal) {
        // Update active state for the global toggle pair
        section.querySelectorAll('.product-plans__toggle-btn').forEach(function (b) {
          var active = b.dataset.type === type;
          b.classList.toggle('product-plans__toggle-btn--active', active);
          b.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        // Propagate to every card in the section
        section.querySelectorAll('.plan-card').forEach(function (card) {
          applyPurchaseTypeToCard(card, type);
        });
      } else {
        var card = btn.closest('.plan-card');
        applyPurchaseTypeToCard(card, type);
      }
    });
  }

  // ──────────────────────────────────────────────
  // Sticky Add-to-Cart bar (product pages only)
  // Shows when .product-hero__ctas scrolls out of viewport
  // ──────────────────────────────────────────────
  function initStickyATC() {
    var bar = document.getElementById('sticky-atc');
    if (!bar) return;

    // Observe the hero CTAs block as the scroll trigger
    var trigger = document.querySelector('.product-hero__ctas');
    if (!trigger) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var heroVisible = entry.isIntersecting;
          bar.classList.toggle('sticky-atc--visible', !heroVisible);
          bar.setAttribute('aria-hidden', heroVisible ? 'true' : 'false');
        });
      },
      { threshold: 0 }
    );

    observer.observe(trigger);
  }

  // ──────────────────────────────────────────────
  // Theme Editor event handling
  // ──────────────────────────────────────────────
  function initEditorEvents() {
    document.addEventListener('shopify:section:load', function () {
      // Section reload handling (no-op for now)
    });
  }

  // ──────────────────────────────────────────────
  // Product card image carousels
  // ──────────────────────────────────────────────
  function initProductCardCarousels() {
    var cards = document.querySelectorAll('.product-card__media--carousel');
    if (!cards.length) return;

    cards.forEach(function (card) {
      var slides = card.querySelector('.product-card__slides');
      var dots = card.querySelectorAll('.product-card__dot');
      var thumbs = card.querySelectorAll('.product-card__thumb');
      if (!slides) return;

      function goToSlide(index) {
        var slideEls = slides.querySelectorAll('.product-card__slide');
        if (!slideEls[index]) return;
        slideEls[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }

      function setActive(index) {
        dots.forEach(function (d, i) {
          d.classList.toggle('product-card__dot--active', i === index);
        });
        thumbs.forEach(function (t, i) {
          t.classList.toggle('product-card__thumb--active', i === index);
        });
      }

      // Click handlers for dots
      dots.forEach(function (dot) {
        dot.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var idx = parseInt(dot.getAttribute('data-slide-index'), 10);
          goToSlide(idx);
          setActive(idx);
        });
      });

      // Click handlers for thumbnails
      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var idx = parseInt(thumb.getAttribute('data-slide-index'), 10);
          goToSlide(idx);
          setActive(idx);
        });
      });

      // Scroll observer to update active dot/thumb on swipe
      var scrollTimeout;
      slides.addEventListener('scroll', function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var slideWidth = slides.offsetWidth;
          if (slideWidth === 0) return;
          var index = Math.round(slides.scrollLeft / slideWidth);
          setActive(index);
        }, 60);
      }, { passive: true });
    });
  }

  // ──────────────────────────────────────────────
  // Product hero carousel
  // ──────────────────────────────────────────────
  function initHeroCarousel() {
    var carousel = document.querySelector('[data-hero-carousel]');
    if (!carousel) return;

    var slides = carousel.querySelector('[data-hero-slides]');
    var dots = carousel.querySelectorAll('.product-hero__dot');
    var thumbs = carousel.querySelectorAll('.product-hero__thumb');
    if (!slides) return;

    function goToSlide(index) {
      var slideEls = slides.querySelectorAll('.product-hero__slide');
      if (!slideEls[index]) return;
      slideEls[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    function setActive(index) {
      dots.forEach(function (d, i) {
        d.classList.toggle('product-hero__dot--active', i === index);
      });
      thumbs.forEach(function (t, i) {
        t.classList.toggle('product-hero__thumb--active', i === index);
      });
    }

    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(dot.getAttribute('data-slide-index'), 10);
        goToSlide(idx);
        setActive(idx);
      });
    });

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(thumb.getAttribute('data-slide-index'), 10);
        goToSlide(idx);
        setActive(idx);
      });
    });

    var scrollTimeout;
    slides.addEventListener('scroll', function () {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function () {
        var slideWidth = slides.offsetWidth;
        if (slideWidth === 0) return;
        var index = Math.round(slides.scrollLeft / slideWidth);
        setActive(index);
      }, 60);
    }, { passive: true });
  }

  // ──────────────────────────────────────────────
  // Init all on DOM ready
  // ──────────────────────────────────────────────
  function init() {
    initMobileMenu();
    initStickyHeader();
    initThemeToggle();
    initProductGallery();
    initAnnouncementBar();
    initCartDrawer();
    initAjaxAddToCart();
    initVariantLinkAddToCart();
    initCartIconDrawer();
    initStickyATC();
    initPlanCardSubscribeToggle();
    initProductCardCarousels();
    initHeroCarousel();
    initEditorEvents();

    // Express checkout: hide block if Shopify rendered no buttons.
    // Also re-check shortly after, since Shopify Pay/Apple Pay buttons
    // can hydrate slightly after initial paint.
    syncExpressCheckoutVisibility();
    window.setTimeout(syncExpressCheckoutVisibility, 600);

    // Cart urgency countdown (only fires if a widget is in the DOM)
    initCartUrgencyCountdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
